import { algorithmTypes } from '../../config/AlgorithmTypes.js';
import api from '../../config/AxiosService';
import { Algorithm, AlgorithmVersion, Model, Pagination } from '../../types';
import { OverviewSummary } from '../../types/result';

export const getModel = async (modelId: string): Promise<Model> => {
  const response = await api.get(`/models/${modelId}`);
  return response.data;
};

export const getModels = async () => {
  const response = await api.get('/model/list');
  return response.data.content;
};

export const getPaginatedModels = async (
  algorithmId: string,
  params: Pagination,
  filter: string
): Promise<{ items: Model[]; totalItems: number }> => {
  const response = await api.get(
    `/models/list/${algorithmId}?page=${params.page}&size=${params.pageSize}` + (filter ? `&name=${filter}` : '')
  );
  return { items: response.data.items, totalItems: response.data.meta.totalItems };
};

export const getOverview = async (algorithmVersionId: string): Promise<Record<string, OverviewSummary>> => {
  const response = await api.post(
    `/scoring/summaries/${algorithmVersionId}`,
    {},
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );

  return response.data;
};

export const getAlgorithms = async (): Promise<Algorithm[]> => {
  const response = await api.get('/algorithms/list');
  return response.data;
};

export const getLatestAlgorithmVersion = async (algorithmId: string): Promise<AlgorithmVersion> => {
  const response = await api.get(`/algorithms/${algorithmId}/version/latest`);
  return response.data;
};

export const getAlgorithmVersionById = async (algorithmVersionId: string): Promise<AlgorithmVersion> => {
  const response = await api.get(`/algorithm-version/${algorithmVersionId}`);
  return response.data;
};

export const getAlgorithmVersions = async (algorithmId: number): Promise<AlgorithmVersion[]> => {
  const response = await api.get(`/algorithms/${algorithmId}/version/list`);
  return response.data;
};

export const getShowableAlgorithmVersions = async (algorithmId: string): Promise<AlgorithmVersion[]> => {
  const response = await api.get(`/algorithm-version/get-all-visible-versions-for-algorithm-id/${algorithmId}`);
  return response.data;
};

export const getAlgorithm = async (algorithmId: string): Promise<undefined | Algorithm> => {
  const algorithmVersion = await getLatestAlgorithmVersion(algorithmId);
  if (!algorithmVersion) {
    return undefined;
  }
  const algorithm = algorithmVersion.algorithm;
  if (!algorithm) return undefined;

  const algorithmType = algorithmTypes.find(type => type.name === algorithm.name);
  return { ...algorithmType, ...algorithm };
};

export const getModelsForAlgorithm = async (algorithmId: number): Promise<Model[]> => {
  const response = await api.get(`/models/list/${algorithmId}`);
  return response.data.items;
};

export const getModelsForAlgorithmVersion = async (algorithmVersionId: string): Promise<Model[]> => {
  const response = await api.get(`/models/list/algorithm-version/${algorithmVersionId}`);
  return response.data.items;
};

export const createScoringForAlgorithm = async (
  algorithmId: number,
  algorithmVersionVersion: string,
  preproduction: boolean
) => {
  const response = await api.post(
    `/scoring/algorithm/${algorithmId}/algorithm-version/${algorithmVersionVersion}?preproduction=${preproduction}`
  );
  return response.data;
};

export const deleteModel = async (modelId: number): Promise<void> => {
  await api.delete(`/models/${modelId}`);
};

export const createModel = async (
  name: string,
  algorithmId: string,
  versionId: string,
  annotationTypes: string[]
): Promise<Model> => {
  const payload = {
    name: name,
    algorithmId: algorithmId,
    versionId: versionId,
    info: {
      annotationTypes: annotationTypes,
    },
  };
  const { data } = await api.post('/models', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
};

export const createMultiModel = async (
  name: string,
  flowName: string | null,
  flowMode: string,
  algorithmId: string,
  versionId: string,
  annotationTypes: string[],
  datasetId: string | null
): Promise<Model> => {
  const payload = {
    name: name,
    algorithmId: algorithmId,
    versionId: versionId,
    info: {
      flowName: flowName,
      flowMode: flowMode,
      active: false,
      annotationTypes: annotationTypes,
    },
    datasetId: datasetId,
  };
  console.log(payload);
  const { data } = await api.post('/models', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
};

export const renameModel = async (name: string, id: number) => {
  const body = { name };
  const { data } = await api.put(`/models/${id}`, body);
  return data;
};

export const editMultiModel = async (
  modelId: number,
  flowMode: string | null,
  flowName: string | null,
  info: any,
  newName?: string | null
) => {
  const payload = {
    ...info,
    flowMode,
    flowName,
  };
  if (newName) {
    const body = { name: newName };
    await api.put(`/models/${modelId}`, body);
  }
  const { data } = await api.post(`/models/${modelId}/change-info`, payload);
  return data;
};

export const updateMultiModel = async (modelId: number, active: boolean, info: any): Promise<void> => {
  const payload = {
    ...info,
    active: active,
  };
  console.log(modelId, payload);
  const response = await api.post(`/models/${modelId}/change-info`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  console.log(response);
};

export const hideModel = async (modelId: number) => {
  const response = await api.post(`/models/${modelId}/hide`);
  return response;
};

export const unhideModel = async (modelId: number) => {
  const response = await api.post(`/models/${modelId}/unhide`);
  return response;
};

export const addModelVersion = async (algorithmId: number, name: string, pretrained: boolean, version: string) => {
  const body = { name, pretrained, version };
  const response = await api.post(`/algorithms/${algorithmId}/create-version`, body);
  return response;
};

export const removeVersion = async (id: string) => {
  const response = await api.delete(`/algorithms/version/${id}`);
  return response;
};
